/**
 * The candidate phone numbers the credit bureau holds for a customer.
 *
 * ## Why this needs its own module
 *
 * When Equifax reports a phone mismatch it does not name ONE number — it names up to three:
 *
 *   "Inquiry parameters:Phone does not match with credit file ~ Phones:|XXXXX29321|XXXXX21056|XXXXX81448"
 *
 * The backend extracts them all and returns them as `extractedNumbers`. The journey, however,
 * stored and displayed only `extractedNumbers[0]`. In production **52% of phone mismatches
 * carry two or three candidates**, so roughly half of these customers were shown the wrong
 * number, entered the wrong number, and stayed stuck no matter how many times they retried.
 * That is a second, independent cause of stuck leads alongside the backend dropping the value.
 *
 * ## Scoping
 *
 * Hints are stored per journey token, not in one global key. Two leads opened in the same
 * browser (an ops person working through a list, a shared device) would otherwise validate
 * against each other's numbers.
 */

const HINTS_KEY_PREFIX = "bureauPhoneHints:";

/**
 * The key the PREVIOUS build wrote — a single global slot holding one number. Read (never
 * written) by `readDisplayHints` so customers already mid-journey at deploy time keep seeing
 * their number. Safe to delete once no in-flight session predates the rollout.
 */
const LEGACY_HINT_KEY = "extractedNumber";

/** The number of trailing digits the bureau reveals. */
const HINT_DIGITS = 5;

const keyFor = (journeyToken) => `${HINTS_KEY_PREFIX}${journeyToken || "unknown"}`;

/**
 * Normalise whatever the API returned into a clean, de-duplicated list of hints.
 * Real payloads repeat values (`|XXXXX51598|XXXXX51598`), so de-duping is not optional.
 */
export function normaliseHints(extractedNumbers) {
  if (!Array.isArray(extractedNumbers)) return [];
  const cleaned = extractedNumbers
    .map((n) => String(n ?? "").trim())
    .filter((n) => /^\d{4,}$/.test(n));
  return [...new Set(cleaned)];
}

/** Persist every hint for this journey. Called when the API returns PHONE_MISMATCH. */
export function storeBureauPhoneHints(journeyToken, extractedNumbers) {
  const hints = normaliseHints(extractedNumbers);
  try {
    if (hints.length) {
      localStorage.setItem(keyFor(journeyToken), JSON.stringify(hints));
    } else {
      // The bureau named no numbers (about 2% of mismatches). Clear any stale list rather
      // than leaving a previous lead's hints in place.
      localStorage.removeItem(keyFor(journeyToken));
    }
  } catch {
    // Storage can be unavailable (private mode, quota). The screen still works — it just
    // cannot show the hint, which is strictly better than crashing the journey.
  }
  return hints;
}

/** Read the hints for this journey. Always returns an array. */
export function readBureauPhoneHints(journeyToken) {
  try {
    const raw = localStorage.getItem(keyFor(journeyToken));
    if (!raw) return [];
    return normaliseHints(JSON.parse(raw));
  } catch {
    return [];
  }
}

/**
 * Hints to SHOW the customer — the per-journey list, or the pre-upgrade value if that is empty.
 *
 * ## Why a second reader exists
 *
 * The build before this one stored the bureau's number in a single GLOBAL key,
 * `localStorage["extractedNumber"]`, and printed it by concatenating onto the configured
 * sub-heading. This module writes a per-journey key instead and does not know the old name, so
 * on the deploy every customer ALREADY sitting on the mismatch screen silently lost the number:
 * the card hid itself and the screen asked them to confirm a number it never showed. Verified in
 * production against a real session (`extractedNumber: 89576`, no `bureauPhoneHints:*`).
 *
 * ## Why this is display-only and NOT used for validation
 *
 * The legacy key is global, so in a browser that opened two leads it holds whichever lead was
 * seen last — it is not reliably THIS journey's number. Showing a possibly-stale number is
 * harmless (the customer recognises their own number or ignores it). Validating against one is
 * not: `matchesAnyHint` would reject the customer's CORRECT number and re-create the dead end
 * this whole change exists to remove. So validation keeps using `readBureauPhoneHints`, which
 * stays strictly per-journey, and an in-flight customer simply passes through unvalidated —
 * exactly as they do when the bureau names no numbers at all.
 *
 * Self-limiting: the first real PHONE_MISMATCH response writes the per-journey key, after which
 * this fallback is never consulted again for that journey.
 */
export function readDisplayHints(journeyToken) {
  const hints = readBureauPhoneHints(journeyToken);
  if (hints.length) return hints;
  try {
    return normaliseHints([localStorage.getItem(LEGACY_HINT_KEY)]);
  } catch {
    return [];
  }
}

/**
 * Human-readable list for the sub-heading: "89576", or "29321, 21056 or 81448".
 * Returns an empty string when there is nothing to show, so the caller can omit the sentence
 * entirely rather than printing "ending with ".
 */
export function formatHintsForDisplay(hints) {
  if (!hints?.length) return "";
  if (hints.length === 1) return hints[0];
  return `${hints.slice(0, -1).join(", ")} or ${hints[hints.length - 1]}`;
}

/**
 * Does the entered number match ANY of the candidates the bureau named?
 *
 * Returns `true` when there are no hints. That is deliberate: about 2% of mismatch responses
 * name no numbers at all, and blocking submission on missing data would convert a recoverable
 * case into a hard dead end — the exact failure mode this whole change exists to remove.
 */
export function matchesAnyHint(phone, hints) {
  if (!hints?.length) return true;
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.length < HINT_DIGITS) return false;
  const tail = digits.slice(-HINT_DIGITS);
  return hints.some((h) => h.slice(-HINT_DIGITS) === tail);
}
